import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { OAuthProvider, signInWithCredential } from "firebase/auth";
import { auth } from "../config/signup.config";
import { toastrError } from "../components/toastr";
import { ref, push, get, getDatabase } from "firebase/database";

function generateNonce(length = 32) {
  const charset =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._";
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values).map((v) => charset[v % charset.length]).join("");
}

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(str)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function processSignIn(user, navigate) {
  localStorage.setItem("email", user.email);
  localStorage.setItem("role", "admin");
  try {
    const database = getDatabase();
    const userRef = ref(database, "user");
    const snapshot = await get(userRef);
    let existingEmails = [];
    if (snapshot.exists()) {
      existingEmails = Object.values(snapshot.val());
    }
    const filteredEmail = existingEmails.filter((i) => i === user.email);
    if (!filteredEmail || filteredEmail.length === 0) {
      await createUser(user.email);
      localStorage.setItem("onboardingComplete", "false");
      navigate("/onboarding");
    } else {
      const userInfoRef = ref(database, "userInfo");
      const infoSnapshot = await get(userInfoRef);
      let onboardingComplete = false;
      if (infoSnapshot.exists()) {
        const allUsers = Object.values(infoSnapshot.val());
        const currentUser = allUsers.find((u) => u.email === user.email);
        if (currentUser) {
          onboardingComplete = currentUser.onboardingComplete === true;
          if (currentUser.id) localStorage.setItem("ownerId", currentUser.id);
        }
      }
      localStorage.setItem(
        "onboardingComplete",
        onboardingComplete ? "true" : "false"
      );
      navigate(onboardingComplete ? "/dashboard" : "/onboarding");
    }
  } catch (err) {
    toastrError("Sign-in processing failed");
  }
}

async function createUser(payload) {
  try {
    const database = getDatabase();
    const userRef = ref(database, "user");
    await push(userRef, payload);
    return true;
  } catch (err) {
    toastrError("Failed to create user");
    throw err;
  }
}

export const appleSignIn = async (navigate, setLoading) => {
  setLoading?.(true);
  try {
    const rawNonce = generateNonce();
    const hashedNonce = await sha256Hex(rawNonce);

    const result = await SignInWithApple.authorize({
      clientId: "com.pazhanam.waitlessq",
      redirectURI: "https://waiting-list-easy.firebaseapp.com/__/auth/handler",
      scopes: "email name",
      nonce: hashedNonce,
    });

    const provider = new OAuthProvider("apple.com");
    const credential = provider.credential({
      idToken: result.response.identityToken,
      rawNonce,
    });

    const firebaseResult = await signInWithCredential(auth, credential);

    // Apple may hide the email after first sign-in; fall back to a placeholder
    const email =
      firebaseResult.user.email ||
      `${firebaseResult.user.uid}@privaterelay.appleid.com`;

    await processSignIn({ ...firebaseResult.user, email }, navigate);
  } catch (err) {
    if (err?.code !== "1001") {
      // 1001 = user cancelled — don't show error
      toastrError("Apple sign-in failed. Please try again.");
    }
    setLoading?.(false);
  }
};
